import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BirthInput from "./BirthInput";
import AuthModal from "../AuthModal";
import LanguagePrompt from "../LanguagePrompt";
import SavedCompatReports from "./SavedCompatReports";
import { useAuth } from "../../context/AuthContext";
import { usePricing } from "../../context/PricingContext";
import { supabase } from "../../supabaseClient";
import { AnalyticsManager } from "../../utils/analytics";
import { AnalyticsEvents } from "../../utils/AnalyticsEvents";
import { setAuthSource } from "../../utils/authSource";
import {
  IconHeartFilled,
  IconCircleCheckFilled,
} from "@tabler/icons-react";
import {
  saveCompatDraft,
  takeCompatDraft,
  clearCompatDraft,
  type CompatDraft,
  type DraftSide,
} from "../../hooks/useCompatMatchDraft";
import { Birth, runGunMilan } from "../../utils/calculatorApi";
import "./VedicCalculator.css";
import "./KundliMatch.css";

interface Koot {
  key: string;
  label: string;
  obtained: number;
  max: number;
  meaning?: string;
  boy?: string;
  girl?: string;
}

interface GunMilan {
  total: number;
  out_of: number;
  koots: Koot[];
}

type Saved = DraftSide;

type Who = "boy" | "girl";

/* The engine labels grahamaitri "Rasi AdhiPathi" and bhakoot "Rasi", which are
   accurate but not what anyone searching for kundli matching recognises. Match
   on the stable key and fall back to the engine's own label if it ever sends a
   koot we have not seen. */
const KOOT_NAMES: Record<string, string> = {
  varna: "Varna",
  vasya: "Vashya",
  vashya: "Vashya",
  tara: "Tara",
  yoni: "Yoni",
  grahamaitri: "Graha Maitri",
  gana: "Gana",
  bhakoot: "Bhakoot",
  nadi: "Nadi",
};

/* Gender comes from the role, not from a question. Several kootas are scored
   directionally from groom to bride, so the two slots are already gendered by
   definition and asking again would be noise. */
const PROFILE_GENDER: Record<Who, string> = { boy: "Male", girl: "Female" };

/* The eight Ashtakoot kootas and their maximum points, for the preview panel
   beside the form. Weights are fixed by the system and total 36. */
const PREVIEW_KOOTAS = [
  { name: "Varna", pts: 1 },
  { name: "Vashya", pts: 2 },
  { name: "Tara", pts: 3 },
  { name: "Yoni", pts: 4 },
  { name: "Graha Maitri", pts: 5 },
  { name: "Gana", pts: 6 },
  { name: "Bhakoot", pts: 7 },
  { name: "Nadi", pts: 8 },
];

const PREVIEW_DOSHAS = ["Mangal dosha", "Nadi dosha", "Bhakoot dosha"];

const titleCase = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

const verdictFor = (total: number, max: number) => {
  const pct = (total / max) * 100;
  if (pct >= 86) return "Excellent match";
  if (pct >= 69) return "Very good match";
  if (pct >= 50) return "Workable match";
  return "Needs a closer look";
};

const readableDate = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

/* Declared at module scope on purpose. When this lived inside KundliMatch it
   was a new component type on every render, so React unmounted and remounted
   both panels whenever either one changed, wiping whatever the user had
   half-typed into the other side. */
const Side: React.FC<{
  label: string;
  /** Decorative only - kept OUT of `label` because label also feeds the
      name input's placeholder, where an emoji does not belong. */
  emoji?: string;
  /** Selected gender for this side, and its setter. Held by the parent so the
      value survives this panel being cleared and re-filled. */
  gender: "Male" | "Female";
  /** Placeholder for the name field. Separate from `label`, which is now a
      phrase ("Your details") rather than a bare noun. */
  namePlaceholder: string;
  onGenderChange: (g: "Male" | "Female") => void;
  hint: string;
  data: Saved | null;
  name: string;
  onNameChange: (v: string) => void;
  onSave: (b: Birth, place: string, timezone?: string) => void;
  onClear: () => void;
}> = ({
  label,
  emoji,
  hint,
  data,
  name,
  onNameChange,
  onSave,
  onClear,
  gender,
  onGenderChange,
  namePlaceholder,
}) => {
  // The name lives here rather than inside BirthInput because BirthInput is
  // shared with the single-chart calculators, which have nobody to name.
  const [nameError, setNameError] = useState("");

  // BirthInput has already validated date, time and place by the time this
  // runs, so the only thing left to check is the one field it does not own.
  const guard = (b: Birth, place: string, timezone?: string) => {
    if (!name.trim()) {
      setNameError("Please add a name so we know whose chart this is.");
      return;
    }
    setNameError("");
    onSave(b, place, timezone);
  };

  return (
    <div className="kmatch-side" data-gender={gender}>
      {/* The emoji is back: it used to live in the step rail, and the rail is
          gone. Sized down and given its own token so it reads as an icon rather
          than as an emoji dropped into a heading. */}
      <h3 className="kmatch-side-title">{label}</h3>

      {/* Gender, as a two-way toggle. Ashtakoot is read with a groom chart and
          a bride chart, so the side already implies it, but stating it makes
          the pair unambiguous and matches what people expect from every other
          matching form. */}
      <div className="kmatch-gender">
          <span className="kmatch-gender__label">Gender</span>
          {/* A segmented slider, not two buttons: one track with a thumb that
              moves between the two halves, so the control reads as a single
              switch with a position rather than a pair of things to compare. */}
          <div
            className="kmatch-gender__track"
            data-on={gender}
            role="group"
            aria-label="Gender"
          >
            <span className="kmatch-gender__thumb" aria-hidden="true" />
            {(["Male", "Female"] as const).map((g) => (
              <button
                key={g}
                type="button"
                className={
                  "kmatch-gender__btn" + (gender === g ? " is-on" : "")
                }
                aria-pressed={gender === g}
                onClick={() => onGenderChange(g)}
              >
                {g}
              </button>
            ))}
          </div>
      </div>
      {false ? (
        <div className="kmatch-done">
          <p className="kmatch-done-name">{data.name}</p>
          <p className="kmatch-done-line">
            <strong>{readableDate(data.birth.date)}</strong> at{" "}
            <strong>{data.birth.time}</strong>
          </p>
          <p className="kmatch-done-place">{data.place}</p>
          <button type="button" className="kmatch-change" onClick={onClear}>
            Change details
          </button>
        </div>
      ) : (
        <>
          {/* The details stay ON SCREEN and editable once saved. They used to
              collapse into a read-only summary card with a "Change details"
              button, which hid what you had entered behind an extra click and
              made the two columns jump to a different size the moment a side
              committed. Nothing is lost: autoCommit re-saves on every edit. */}
          <label className="birthinput-field kmatch-name">
            <span>Name</span>
            <input
              type="text"
              value={name}
              placeholder={namePlaceholder}
              autoComplete="off"
              maxLength={40}
              onChange={(e) => {
                onNameChange(e.target.value);
                if (nameError) setNameError("");
              }}
            />
          </label>
          {nameError ? <p className="birthinput-error">{nameError}</p> : null}
          {/* "Confirm", not "Save". English overloads save (store vs rescue) and
              translation engines pick rescue - the Hindi build rendered
              "Save groom" as "दूल्हे को बचाओ", i.e. rescue the groom. Confirm has
              one meaning everywhere. The name is dropped because the 👨/👩
              heading directly above already says whose card this is, and it
              pairs with the "Change details" button that replaces it. */}
          {/* No per-side Confirm. Two confirm buttons for one action is a step
              the visitor should not have to take: the details commit as soon as
              date, time and place are all present. */}
          <BirthInput
            onSubmit={guard}
            cta="Confirm details"
            autoCommit
            recommitKey={name}
            alwaysSplitTime
            offerUnknownTime
          />
        </>
      )}
    </div>
  );
};

/* Three steps: save the groom, save the bride, then match. The scoring is the
   engine's own gun_milan, not ours. Several kootas are directional, so which
   birth goes in which slot changes the answer and the labels have to be
   unmissable.

   The score sits behind a phone number. That gate is also the lead capture:
   the source rides along with OTP verification and the backend stamps it on the
   new user, which is how marketing gets the phone list without a new table. */
const KundliMatch: React.FC = () => {
  const {
    user,
    pricingVariant,
    signInWithGoogle,
    requestPhoneOtp,
    verifyPhoneOtp,
    requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp,
    verifyMsg91Otp,
    effectiveBalance,
  } = useAuth();
  const { prices, formatPrice } = usePricing();
  const navigate = useNavigate();

  /* Same resolution CompatibilityScreen uses, so the price in this popup and
     the price the server actually charges cannot drift apart. Note there is no
     `compatibility_report` row in service_prices today, so this resolves via
     report_premium (INR 10000 = Rs 100 after formatPrice's /100). */
  const reportCost =
    prices["compatibility_report"] || prices["report_premium"] || 10;
  const canAfford = effectiveBalance !== null && effectiveBalance >= reportCost;

  const [payOpen, setPayOpen] = useState(false);
  /* Which side is on screen. Completing one hands the visitor to the other
     instead of making them find the tab. */
  const [boy, setBoy] = useState<Saved | null>(null);
  const [girl, setGirl] = useState<Saved | null>(null);
  // Kept in the parent for the same reason Side is declared at module scope:
  // so typing in one panel cannot wipe what is half-entered in the other.
  const [boyName, setBoyName] = useState("");
  const [girlName, setGirlName] = useState("");
  const [boyGender, setBoyGender] = useState<"Male" | "Female">("Male");
  const [girlGender, setGirlGender] = useState<"Male" | "Female">("Female");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");
  const [match, setMatch] = useState<GunMilan | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [notReady, setNotReady] = useState("");
  const [openingChat, setOpeningChat] = useState(false);

  // Guards the post-login resume against StrictMode's double effect, which
  // would otherwise fire the scoring call twice on one return.
  const resumedRef = useRef(false);
  // Fired once per completed spread so re-renders do not re-report the step.
  const readyReportedRef = useRef(false);

  const score = useCallback(async (b: Saved, g: Saved) => {
    setBusy(true);
    setFailed("");
    try {
      const result = await runGunMilan<GunMilan>(b.birth, g.birth);
      setMatch(result);
      AnalyticsManager.trackEvent(AnalyticsEvents.CompatCalcScoreViewed, {
        total: result.total,
        out_of: result.out_of,
      });
    } catch (e) {
      setFailed(e instanceof Error ? e.message : "Could not read those charts.");
    } finally {
      setBusy(false);
    }
  }, []);

  const save =
    (who: Who) => (birth: Birth, place: string, timezone?: string) => {
      setFailed("");
      setMatch(null);
      const name = (who === "boy" ? boyName : girlName).trim();
      const sideData: Saved = { birth, place, timezone, name };
      /* Hand them to the empty side once this one is complete, but not
         instantly: picking a city is the last field, and switching on the same
         tick pulls the screen away while they are still reading what they
         entered. A short beat lets the summary card land and the step tick
         turn green first, so the move reads as a consequence rather than a
         glitch. */
      if (who === "boy") {
        setBoy(sideData);
      } else {
        setGirl(sideData);
      }
    };

  const clear = (who: Who) => () => {
    setFailed("");
    setMatch(null);
    readyReportedRef.current = false;
    if (who === "boy") setBoy(null);
    else setGirl(null);
  };

  const ready = Boolean(boy && girl);

  /* Pressing the button when a side is missing names the side that is missing,
     rather than leaving a disabled control and a generic hint. */
  const attempt = () => {
    if (boy && girl) {
      setNotReady("");
      run();
      return;
    }
    const missing = !boy && !girl
      ? "both sets of birth details"
      : !boy
        ? "the male's birth details"
        : "the female's birth details";
    setNotReady(`Add ${missing} to match the charts.`);
  };

  useEffect(() => {
    if (ready) setNotReady("");
  }, [ready]);

  useEffect(() => {
    if (ready && !readyReportedRef.current) {
      readyReportedRef.current = true;
      AnalyticsManager.trackEvent(AnalyticsEvents.CompatCalcReady, {});
    }
  }, [ready]);

  /* Coming back from login. The draft is single-use, so a remount cannot replay
     the same match twice. */
  useEffect(() => {
    if (!user || resumedRef.current) return;
    const draft = takeCompatDraft();
    if (!draft) return;
    resumedRef.current = true;
    setBoy(draft.boy);
    setGirl(draft.girl);
    setBoyName(draft.boy.name || "");
    setGirlName(draft.girl.name || "");

    // This is the lead: a phone number verified because the visitor wanted the
    // score. Meta gets it as the STANDARD `Lead` event rather than a custom one,
    // because campaign optimisation and the Ads Manager lead columns only read
    // standard events — a trackCustom here would be invisible to the people
    // running the campaign.
    AnalyticsManager.trackEvent(AnalyticsEvents.CompatCalcLead, {
      source: "compatibility_check",
    });
    try {
      const fbq = (window as any).fbq;
      if (fbq) fbq("track", "Lead", { content_name: "compatibility_check" });
    } catch {
      /* pixel blocked; the custom event above still went out */
    }

    void score(draft.boy, draft.girl);
  }, [user, score]);

  const run = () => {
    if (!boy || !girl || busy) return;

    if (!user) {
      // Park the draft BEFORE opening the modal. Google sign-in is a full page
      // reload, so without this the visitor fills in two complete sets of birth
      // details, signs in, and lands back on an empty calculator — at the exact
      // moment they have just handed over their phone number.
      saveCompatDraft({ boy, girl } as CompatDraft);
      setAuthSource("compatibility_check");
      AnalyticsManager.trackEvent(AnalyticsEvents.CompatCalcGateShown, {});
      setAuthOpen(true);
      return;
    }
    void score(boy, girl);
  };

  /* Turn the two births into real profiles and hand off to the compatibility
     chat.

     Mirrors ChatPage's own partner path (user_profiles -> user_birth_details,
     then its endpoint), deliberately: that is the path already proven in
     production. What this does NOT have to do is geocode. ChatPage warns at
     length that its in-chat partners arrive as free text with no coordinates,
     which produced profiles that could never generate a chart. Here the places
     came from the autocomplete, so latitude, longitude and zone are already
     known and that whole failure mode does not exist. */
  const openReport = async () => {
    if (!user || openingChat) return;
    setOpeningChat(true);
    setFailed("");
    AnalyticsManager.trackEvent(AnalyticsEvents.CompatCalcChatClicked, {});

    const created: string[] = [];
    try {
      const makeProfile = async (who: Who, side: Saved) => {
        const { data: profile, error: pErr } = await supabase
          .from("user_profiles")
          .insert({ user_id: user.id, name: side.name })
          .select()
          .single();
        if (pErr) throw new Error(pErr.message || "Could not save the profile.");
        created.push(profile.id);

        const { error: bErr } = await supabase
          .from("user_birth_details")
          .insert({
            profile_id: profile.id,
            date_of_birth: side.birth.date,
            time_of_birth: side.birth.time,
            birth_place: side.place,
            birth_lat: String(side.birth.lat),
            birth_lng: String(side.birth.lon),
            birth_timezone: side.timezone || "Asia/Kolkata",
            // Not optional: the chart generator reads it, and coordinates with
            // a NULL offset produce no chart at all.
            timezone_offset: side.birth.tz,
            gender: PROFILE_GENDER[who],
          });
        if (bErr) throw new Error(bErr.message || "Could not save birth details.");
        return profile.id as string;
      };

      const boyId = await makeProfile("boy", boy!);
      const girlId = await makeProfile("girl", girl!);

      // Link the pair in compatibility_profiles the way ChatPage's
      // handleUseExistingPartner does: partner_user_profile_id points at a real
      // user_profiles row that already has a chart, which is what the compat
      // report and the in-chat compat function both validate against.
      // Non-fatal on failure — the report screen can still be handed the pair
      // through navigation state, and the compat function auto-links server
      // side.
      let compatRowId: string | null = null;
      try {
        const { data: link } = await supabase
          .from("compatibility_profiles")
          .insert({
            user_id: user.id,
            source_profile_id: boyId,
            partner_user_profile_id: girlId,
            partner_name: girl!.name,
            partner_gender: PROFILE_GENDER.girl,
            date_of_birth: girl!.birth.date,
            time_of_birth: girl!.birth.time,
            birth_place: girl!.place,
            birth_lat: String(girl!.birth.lat),
            birth_lng: String(girl!.birth.lon),
            birth_timezone: girl!.timezone || "Asia/Kolkata",
          })
          .select()
          .single();
        compatRowId = link?.id ?? null;
      } catch {
        /* link is an optimisation, not a requirement */
      }

      // Pin the partner now, so the chat CTA on the REPORT screen has nothing
      // left to set up — ChatPage restores this key for whichever profile is
      // selected and runs in compatibility mode from there.
      localStorage.setItem(
        `aura_partner_${boyId}`,
        JSON.stringify({ id: girlId, name: girl!.name }),
      );
      // The report screen reads its own side from here (option A: the pair the
      // visitor just matched becomes their selected profile).
      localStorage.setItem("selectedProfileId", boyId);
      clearCompatDraft();

      // Everything the report needs to show both people WITHOUT asking again.
      navigate("/compatibility-report", {
        state: {
          fromCalculator: true,
          // Paid for at the popup, so the report screen should generate on
          // arrival rather than showing another button. The ACTUAL charge is
          // still the server-side hold in handleGenerateReport - this flag only
          // says "the visitor already agreed to the price".
          autoGenerate: true,
          sourceProfileId: boyId,
          partnerProfileId: compatRowId,
          partnerUserProfileId: girlId,
          partnerName: girl!.name,
          partnerBirth: {
            date_of_birth: girl!.birth.date,
            time_of_birth: girl!.birth.time,
            birth_place: girl!.place,
            gender: PROFILE_GENDER.girl,
          },
        },
      });
    } catch (e) {
      // Half-created profiles would show up in their dashboard as nameless
      // duplicates on the next attempt, so undo what landed.
      for (const id of created) {
        await supabase.from("user_profiles").delete().eq("id", id);
      }
      setFailed(
        e instanceof Error ? e.message : "Could not open the report right now.",
      );
      setOpeningChat(false);
    }
  };

  const phoneAuthHandlers = useMemo(
    () => ({ requestOtp: requestPhoneOtp, verifyOtp: verifyPhoneOtp }),
    [requestPhoneOtp, verifyPhoneOtp],
  );
  const firebasePhoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestFirebasePhoneOtp,
      verifyOtp: verifyFirebasePhoneOtp,
    }),
    [requestFirebasePhoneOtp, verifyFirebasePhoneOtp],
  );
  const msg91AuthHandlers = useMemo(
    () => ({
      verifyOtp: (payload: {
        accessToken: string;
        phone: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        countryCode?: string;
      }) =>
        verifyMsg91Otp({ ...payload, variant: pricingVariant || "control" }),
    }),
    [verifyMsg91Otp, pricingVariant],
  );

  return (
    <div className="vedcalc">
      {/* Kundli matching draws a lot of non-English traffic, and the only
          language control until now was a two-letter chip in the navbar. */}
      <LanguagePrompt message="Prefer Hindi or another language? You can read this page, and your match result, in your own language." />

      {/* Form on the left, a preview of the result on the right.
          The comparison that prompted this: Astrotalk pairs its form with a
          "Saved Kundli" list, which is empty for every first-time visitor and
          is exactly who a search result delivers. Ours shows what the match
          will actually return, so the panel earns its place on the first visit
          rather than the tenth, and it puts the eight koota names (the terms
          this page is trying to rank for) into the layout honestly. */}
      <div className="kmatch-layout">
      <div className="kmatch-card">
        <div className="kmatch-duo">
          <Side
            label="Your details"
            namePlaceholder="Your name"
            hint="His name and birth details"
            data={boy}
            name={boyName}
            onNameChange={setBoyName}
            onSave={save("boy")}
            onClear={clear("boy")}
            gender={boyGender}
            onGenderChange={setBoyGender}
          />
          {/* Purely decorative. The two panels are the two charts being
              brought together, and a plus between them says that faster than a
              divider does. Hidden from assistive tech: it is punctuation, not
              content, and the headings already name both sides. */}
          <div className="kmatch-plus" aria-hidden="true">
            <span>+</span>
          </div>
          <Side
            label="Partner's details"
            namePlaceholder="Partner's name"
            hint="Her name and birth details"
            data={girl}
            name={girlName}
            onNameChange={setGirlName}
            onSave={save("girl")}
            onClear={clear("girl")}
            gender={girlGender}
            onGenderChange={setGirlGender}
          />
        </div>

        <div className="kmatch-run">
          {/* ALWAYS enabled. A greyed button with "this will turn on" under it
              makes the visitor work out for themselves what is missing; a live
              button that says what is missing when pressed does not. */}
          <button
            type="button"
            className="kmatch-generate"
            onClick={attempt}
            disabled={busy}
          >
            {busy ? "Reading both charts…" : "Match the charts"}
          </button>
          {notReady ? (
            <p className="kmatch-error" role="alert">
              {notReady}
            </p>
          ) : !user ? (
            <p className="kmatch-hint">
              We will send a one-time code to your number, then show the score.
            </p>
          ) : null}
          {/* Mobile-only: a full-width Saved Reports button directly under the
              primary CTA, with the list opening straight underneath it.
              It used to toggle the DESKTOP list, which lives inside the score
              panel and is hidden at this width, so the button opened something
              nobody could see. */}
          {user ? (
            <div className="kmatch-saved-m">
              <button
                type="button"
                className="kmatch-saved-mobile"
                aria-expanded={savedOpen}
                onClick={() => setSavedOpen((v) => !v)}
              >
                Saved Reports
                <span
                  className={"kmatch-saved-caret" + (savedOpen ? " is-open" : "")}
                  aria-hidden="true"
                />
              </button>
              {savedOpen ? (
                <div className="kmatch-saved-menu kmatch-saved-menu--m">
                  <SavedCompatReports variant="page" limit={20} />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

        {/* ALWAYS mounted. Unmounting it once a match existed left the grid's
            second column empty and the form snapped to the narrower first
            column: the layout "shift" on generating a report. It also threw
            away the obvious place to put the score. So it stays, and fills in
            with the real numbers instead. */}
        <aside
          className={`kmatch-preview${match ? " is-scored" : ""}`}
          aria-label={match ? "Your gun milan score" : "What your result will show"}
        >
          <p className="kmatch-preview__eyebrow">
            {match ? "Your result" : "Your result will show"}
          </p>
          <div className="kmatch-preview__score">
            {match ? (
              <span className="kmatch-preview__num">{match.total}</span>
            ) : (
              <span className="kmatch-preview__num" aria-hidden="true">&mdash;</span>
            )}
            <span className="kmatch-preview__den">/ {match ? match.out_of : 36}</span>
          </div>
          {match ? (
            <div className="kmatch-preview__bar">
              <span style={{ width: `${(match.total / match.out_of) * 100}%` }} />
            </div>
          ) : null}
          <p className="kmatch-preview__cap">
            {match ? verdictFor(match.total, match.out_of) : "Total gun milan score"}
          </p>
          <ul className="kmatch-preview__list">
            {match
              ? match.koots.map((k) => (
                  <li key={k.key} className={k.obtained === 0 ? "is-zero" : ""}>
                    <span>{KOOT_NAMES[k.key] || k.label}</span>
                    <span className="kmatch-preview__pts">
                      {k.obtained}
                      <span className="kmatch-preview__of">/{k.max}</span>
                    </span>
                  </li>
                ))
              : PREVIEW_KOOTAS.map((k) => (
                  <li key={k.name}>
                    <span>{k.name}</span>
                    <span className="kmatch-preview__pts">{k.pts}</span>
                  </li>
                ))}
          </ul>
          {/* The doshas were a footnote saying they get checked. As rows they
              carry the same information, fill the panel the form's height
              leaves over, and name three more things people search for. */}
          <p className="kmatch-preview__eyebrow kmatch-preview__eyebrow--sub">
            Also checked
          </p>
          <ul className="kmatch-preview__list kmatch-preview__list--dosha">
            {PREVIEW_DOSHAS.map((d) => (
              <li key={d}>
                <span>{d}</span>
                <span className="kmatch-preview__both">both charts</span>
              </li>
            ))}
          </ul>

          {/* A visitor who paid, read the report and pressed back had no way to
              find it again. Sits under the score panel so the second row of the
              box is CTA on the left, this on the right, rather than both stacked
              under the form with the panel's column left empty. */}
          {user ? (
            <div className="kmatch-savedwrap">
              <button
                type="button"
                className="kmatch-saved-link"
                aria-expanded={savedOpen}
                onClick={() => setSavedOpen((v) => !v)}
              >
                View saved reports
                <span
                  className={"kmatch-saved-caret" + (savedOpen ? " is-open" : "")}
                  aria-hidden="true"
                />
              </button>
              {savedOpen ? (
                <div className="kmatch-saved-menu">
                  <SavedCompatReports variant="page" limit={20} />
                </div>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>



      {failed ? (
        <div className="vedcalc-result vedcalc-failed" role="status">
          <p>{failed}</p>
        </div>
      ) : null}

      {match ? (
        <div className="vedcalc-result kmatch-result" role="status">
          {/* No score here. It lives in the panel beside the form, which is
              where the eye already is when the result lands; repeating it below
              was two copies of one number and an ambiguous target for anything
              reading the page. This block is the ANALYSIS. */}
          <h3 className="kmatch-result-title">Koota by koota</h3>
          <table className="kmatch-table">
            <thead>
              <tr>
                <th>Koota</th>
                <th>Score</th>
                <th>What it covers</th>
              </tr>
            </thead>
            <tbody>
              {match.koots.map((k) => (
                <tr key={k.key} className={k.obtained === 0 ? "is-zero" : ""}>
                  <td>{KOOT_NAMES[k.key] || k.label}</td>
                  <td>
                    {k.obtained}/{k.max}
                  </td>
                  <td>
                    {k.boy && k.girl ? (
                      <span className="kmatch-pair">
                        {titleCase(k.boy)} and {titleCase(k.girl)}.{" "}
                      </span>
                    ) : null}
                    {k.meaning}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Teaser: the note sits behind a blur with the gradient CTA
              centred on top, so the detail reads as "there is more here" and
              the tap goes through to chat. Once blurred the copy is
              decorative, hence aria-hidden and inert - the button carries the
              real action and the real label. */}
          {/* The opening lines stay readable so the score still teaches
              something on its own. Everything after that is blurred with the
              unlock CTA centred on it, which is what makes the rest feel
              worth opening. The blurred copy is decorative once obscured,
              hence aria-hidden and inert - the button carries the action. */}
          <p className="vedcalc-note kmatch-note-lead">
            18 out of 36 is the traditional threshold for a match to be
            considered workable, and 28 and above is treated as excellent.
          </p>

          <div className="kmatch-teaser">
            <p className="vedcalc-note kmatch-teaser-text" aria-hidden="true">
              Several kootas are scored in one direction, from the groom to the
              bride, which is why the two charts are not interchangeable. A
              zero on Bhakoot or Nadi is what astrologers look at hardest,
              though both have recognised cancellations that depend on the full
              charts.
            </p>

            <div className="kmatch-teaser-overlay">
              <button
                type="button"
                className="vedcalc-cta kmatch-cta"
                onClick={() => setPayOpen(true)}
                disabled={openingChat}
              >
                <span aria-hidden="true">🔓</span>{" "}
                {openingChat ? "Opening both charts…" : "Unlock deeper analysis"}
              </button>
            </div>
          </div>

        </div>
      ) : null}

      {/* Pay confirmation. Deliberately a PRE-CHECK, not a payment: the coins
          are taken server-side by place_report_hold_compat inside
          handleGenerateReport, which reserves on start and only settles on a
          successful report. Nothing here debits anything. If the balance is
          short we send them to /wallet rather than half-starting a flow that
          the server would refuse a moment later. */}
      {/* Pay confirmation. A PRE-CHECK, not a payment: coins are taken
          server-side by place_report_hold_compat inside handleGenerateReport,
          which reserves on start and settles only on success. Nothing here
          debits anything. Short balance goes to /wallet rather than half
          starting a flow the server would refuse a moment later. */}
      {payOpen && (
        <div
          className="kmatch-pay-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kmatch-pay-title"
          onClick={() => !openingChat && setPayOpen(false)}
        >
          <div className="kmatch-pay" onClick={(e) => e.stopPropagation()}>
            <span className="kmatch-pay-emblem" aria-hidden="true">
              <IconHeartFilled size={26} />
            </span>

            <h3 className="kmatch-pay-title" id="kmatch-pay-title">
              Full compatibility report
            </h3>
            <p className="kmatch-pay-sub">
              {boy?.name} and {girl?.name}, read as one chart.
            </p>

            <ul className="kmatch-pay-list">
              {[
                "All 36 gunas explained, not just the score",
                "Manglik dosha checked on both sides",
                "The years that matter for this match",
                "Remedies chosen for the two of you",
              ].map((line) => (
                <li key={line}>
                  <IconCircleCheckFilled size={16} aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              className="kmatch-pay-btn"
              disabled={openingChat || effectiveBalance === null}
              onClick={() => {
                if (!canAfford) {
                  setPayOpen(false);
                  navigate("/wallet");
                  return;
                }
                openReport();
              }}
            >
              {openingChat
                ? "Opening your report..."
                : `${formatPrice(reportCost)} \u00b7 Unlock full report`}
            </button>

            <button
              type="button"
              className="kmatch-pay-cancel"
              onClick={() => setPayOpen(false)}
              disabled={openingChat}
            >
              Not now
            </button>
          </div>
        </div>
      )}

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        phoneAuth={phoneAuthHandlers}
        firebasePhoneAuth={firebasePhoneAuthHandlers}
        msg91Auth={msg91AuthHandlers}
      />
    </div>
  );
};

export default KundliMatch;
